import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { Router } from '@angular/router';
import { defer, forkJoin, Observable, Subject, timer } from 'rxjs';
import { finalize, flatMap, takeUntil, tap } from 'rxjs/operators';
import { ResultWithTotal } from 'src/app/api/models/OK/Egis/Service/Business/result-with-total';
import { RecentEftersynDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/recent-eftersyn-dto';
import { SystemStatusService } from 'src/app/api/services';
import { EGIS_SERVICE_CALLER } from 'src/app/constants';
import { LoaderService } from 'src/app/services/loader.service';

@Component({
    selector: 'app-system-status-eftersyn',
    templateUrl: './system-status-eftersyn.component.html',
    styleUrls: ['./system-status-eftersyn.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class SystemStatusEftersynComponent implements OnInit, OnDestroy {
  TIMER = 60000;

  columnsEftersyn: string[] = ['timestamp', 'oKAftaleNr', 'ordreNo'];
  columnsAftaleQueue: string[] = ['timestamp', 'oKAftaleNr'];

  unsubscribe$ = new Subject<void>();

  nextScheduledEftersynCreation: Date;

  recentEftersynLoading$ = new Subject<boolean>();

  recentEftersyn: RecentEftersynDto[];

  waitingEftersynCreatedLoading$ = new Subject<boolean>();

  waitingEftersynCreated: [] | null;
  waitingEftersynCreatedTotal: number | null;

  getNextScheduledEftersynCreation$: Observable<string>;
  getRecentEftersyn$: Observable<RecentEftersynDto[]>;
  getWaitingEftersynCreated$: Observable<ResultWithTotal>;

  constructor(private systemStatusService: SystemStatusService, private router: Router, private loader: LoaderService) {}

  ngOnInit(): void {
    let waitingEftersynCreatedLoaderRef = this.loader.createLoader();
    waitingEftersynCreatedLoaderRef.showStateChanged$.subscribe((shouldShow: boolean) => {
      this.waitingEftersynCreatedLoading$.next(shouldShow);
    });

    let recentEftersynLoaderRef = this.loader.createLoader();
    recentEftersynLoaderRef.showStateChanged$.subscribe((shouldShow: boolean) => {
      this.recentEftersynLoading$.next(shouldShow);
    });

    this.getNextScheduledEftersynCreation$ = defer(() => {
      return this.systemStatusService.getNextScheduledEftersynCreation({ caller: EGIS_SERVICE_CALLER }).pipe(
        tap((result: string) => {
          this.nextScheduledEftersynCreation = new Date(result);
        }),
        takeUntil(this.unsubscribe$),
        finalize(() => {})
      );
    });

    this.getRecentEftersyn$ = defer(() => {
      recentEftersynLoaderRef.start();
      return this.systemStatusService.getRecentEftersyn({ caller: EGIS_SERVICE_CALLER }).pipe(
        tap((result: RecentEftersynDto[]) => {
          this.recentEftersyn = result;
        }),
        takeUntil(this.unsubscribe$),
        finalize(() => {
          recentEftersynLoaderRef.stop();
        })
      );
    });

    this.getWaitingEftersynCreated$ = defer(() => {
      waitingEftersynCreatedLoaderRef.start();
      return this.systemStatusService.getWaitingEftersynCreated({ caller: EGIS_SERVICE_CALLER }).pipe(
        tap((result: ResultWithTotal) => {
          this.waitingEftersynCreated = result.result;
          this.waitingEftersynCreatedTotal = result.count;
        }),
        takeUntil(this.unsubscribe$),
        finalize(() => {
          waitingEftersynCreatedLoaderRef.stop();
        })
      );
    });

    timer(0, this.TIMER)
      .pipe(
        takeUntil(this.unsubscribe$),
        flatMap(() => {
          return forkJoin([this.getNextScheduledEftersynCreation$, this.getRecentEftersyn$, this.getWaitingEftersynCreated$]);
        })
      )
      .subscribe(
        () => {},
        (error) => {
          this.handleError(error);
        }
      );
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.unsubscribe();
  }

  onRowClick(row: any) {
    this.router.navigate(['/aftale', row.okKundeAftaleNr]);
  }

  handleError(error: any) {
    console.log('error', error);
  }
}
